import { 
  Input,
  Optional,
  Directive,
  ElementRef,
  DestroyRef,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * enums
 */
import { FormFieldStatus, IFormFieldStatus } from './enums/form-field-status';


/**
 * @directive FormFieldStatusDirective
 * @description
 * **Angular directive for managing form field validation status** and visual feedback.
 *
 * This directive provides automatic validation state management for form fields, including
 * visual feedback through CSS classes and programmatic access to validation states.
 * It integrates with Angular's `NgControl` to automatically detect validation states
 * and applies appropriate styling based on field validity and touch status.
 *
 * **Key Features:**
 * - 🎯 **Automatic Status Detection** - Integrates with Angular's NgControl
 * - 🎨 **Visual Feedback** - Applies Bootstrap validation classes
 * - 🛡️ **Status Filtering** - Configurable status ignore patterns
 * - 🎪 **Touch State Management** - Handles touched/untouched states
 * - 🔍 **Programmatic Access** - Exported as 'formFieldStatusApi'
 * - 🏷️ **Host Bindings** - Dynamic CSS class application
 *
 * **Common Use Cases:**
 * - Form field validation feedback
 * - Real-time validation state display
 * - Custom validation styling
 * - Form submission state management
 * - Accessibility compliance
 *
 * @example
 * <!-- Basic usage with automatic status detection -->
 * <input appFormFieldStatus
 *   type="text"
 *   formControlName="email"
 *   placeholder="Enter email">
 *
 * @example
 * <!-- With custom status filtering -->
 * <input appFormFieldStatus
 *   type="password"
 *   formControlName="password"
 *   [ignoreStatus]="FormFieldStatus.Valid"
 *   placeholder="Enter password">
 */
/**
 * @directive FormFieldStatusDirective
 * @description
 * **Angular directive for managing form field validation status** and visual feedback.
 *
 * This directive provides automatic validation state management for form fields, including
 * valid/invalid status display, touched state tracking, and customizable status visibility.
 * It integrates with Angular's `NgControl` to automatically detect form control states
 * and applies appropriate CSS classes for visual feedback.
 *
 * **Key Features:**
 * - 🎯 **Automatic Status Detection** - Integrates with Angular form controls
 * - ✅ **Visual Feedback** - Applies Bootstrap validation classes
 * - 🎪 **Customizable Display** - Configurable status visibility
 * - 🏷️ **Host Bindings** - Dynamic CSS class application
 * - 🛡️ **Type Safety** - Type-safe status management
 * - 🎨 **Bootstrap Integration** - Uses Bootstrap validation classes
 *
 * **Common Use Cases:**
 * - Form field validation feedback
 * - Real-time validation status display
 * - Custom validation state management
 * - Form field styling based on validation
 * - Conditional validation display
 *
 * @example
 * <!-- Basic usage with form control -->
 * <input appFormFieldStatus
 *   type="text"
 *   formControlName="email"
 *   placeholder="Enter email">
 *
 * @example
 * <!-- Custom status management -->
 * <input appFormFieldStatus
 *   type="text"
 *   [hostControl]="customControl"
 *   [ignoreStatus]="FormFieldStatus.Valid"
 *   placeholder="Custom validation">
 */
@Directive({
  exportAs: 'formFieldStatusApi',
  selector: '[appFormFieldStatus]',
  host: {
    '[class.is-valid]': 'showValid',
    '[class.is-invalid]': 'showInvalid',
  }
})
export class FormFieldStatusDirective implements OnInit {

  /**
   * @property ignoreStatus
   * @description
   * **Configures which validation statuses to ignore** when applying visual feedback.
   *
   * This property accepts `IFormFieldStatus` enum values to control which validation
   * states should be suppressed.
   *
   * @type {IFormFieldStatus}
   * @default FormFieldStatus.Invalid
   * @example
   * <!-- Ignore both status -->
   * <input appFormFieldStatus [ignoreStatus]="FormFieldStatus.Both">
   * <input appFormFieldStatus [ignoreStatus]="'both'">
   *
   * @example
   * <!-- Ignore valid status -->
   * <input appFormFieldStatus [ignoreStatus]="FormFieldStatus.Valid">
   * <input appFormFieldStatus [ignoreStatus]="'valid'">
   */
  @Input({ alias: 'ignoreStatus' })
  public ignoreStatus!: IFormFieldStatus

  /**
   * @property hostControl
   * @description
   * **Explicitly provides a form control** for status detection.
   *
   * This property allows you to specify a custom `NgControl` instance for status
   * detection instead of relying on the automatically injected control. Useful for
   * complex form scenarios where the directive needs to monitor a different control.
   *
   * @type {NgControl | undefined}
   * @example
   * <!-- With explicit control reference -->
   * <input appFormFieldStatus
   *   [hostControl]="customControl"
 *   type="text"
 *   placeholder="Custom control">
   */
  @Input({ alias: 'hostControl' })
  public hostControl?: NgControl

  /**
   * @property touched
   * @description
   * **Controls the touched state** of the form field.
   *
   * This property allows you to manually set the touched state or automatically
   * detects it from the associated form control. The touched state determines
   * when validation feedback should be displayed.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Manual touched state -->
   * <input appFormFieldStatus [touched]="true" type="text">
   *
   * @example
   * <!-- Automatic touched detection -->
   * <input appFormFieldStatus formControlName="email" type="text">
   */
  @Input({ alias: 'touched' })
  public set touched(value: boolean) {
    this._touched = Boolean(value);
  }
  public get touched(): boolean {
    return this.control?.touched ?? this._touched
  }
  public _touched : boolean = false

  /**
   * @property valid
   * @description
   * **Controls the valid state** of the form field.
   *
   * This property allows you to manually set the valid state or automatically
   * detects it from the associated form control. The valid state determines
   * whether the field passes validation.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Manual valid state -->
   * <input appFormFieldStatus [valid]="true" type="text">
   *
   * @example
   * <!-- Automatic valid detection -->
   * <input appFormFieldStatus formControlName="email" type="text">
   */
  @Input({ alias: 'valid' })
  public set valid(value: boolean) {
    this._valid = Boolean(value);
  }
  public get valid(): boolean {
    return this.control?.valid ?? this._valid
  }
  public _valid: boolean = false
  
  /**
   * @property invalid
   * @description
   * **Computed invalid state** based on touched and valid properties.
   *
   * This getter calculates whether the field should be considered invalid.
   * A field is invalid when it has been touched and is not valid.
   *
   * @type {boolean}
   * @example
   * <!-- Invalid state is automatically computed -->
   * <input appFormFieldStatus formControlName="email" type="text">
   * <!-- Shows invalid styling when touched and invalid -->
   */
  public get invalid(): boolean {
    return this.touched && !this.valid;
  }  

  /**
   * @property control
   * @description
   * **Gets the associated form control** for status detection.
   *
   * This getter returns the form control that this directive is monitoring.
   * It prioritizes the explicitly provided `hostControl` over the automatically
   * injected `NgControl` for maximum flexibility.
   *
   * @type {AbstractControl | null}
   * @example
   * // Access the control programmatically
   * const control = this.formFieldStatus.control;
   * if (control) {
   *   console.log('Control value:', control.value);
   *   console.log('Control valid:', control.valid);
   * }
   */
  public get control(): AbstractControl | null {
    return this.hostControl ? this.hostControl?.control : this._ngControl?.control
  }

  /**
   * @property showInvalid
   * @description
   * **Determines if invalid status should be shown** based on current state and ignore settings.
   *
   * This getter calculates whether the invalid status should be displayed. It checks
   * if the field is invalid and whether the invalid status is not being ignored
   * through the `ignoreStatus` property.
   *
   * @type {boolean}
   * @example
   * <!-- Invalid status shown when field is touched and invalid -->
   * <input appFormFieldStatus formControlName="email" type="text">
   * <!-- Shows red border and styling when invalid -->
   */
  public get showInvalid(): boolean {
    return !!this.control?.enabled && this.invalid && this.ignoreStatus !== FormFieldStatus.Invalid;
  }

  /**
   * @property showValid
   * @description
   * **Determines if valid status should be shown** based on current state and ignore settings.
   *
   * This getter calculates whether the valid status should be displayed. It checks
   * if the field has been touched, is valid, and whether the valid status is not
   * being ignored through the `ignoreStatus` property.
   *
   * @type {boolean}
   * @example
   * <!-- Valid status shown when field is touched and valid -->
   * <input appFormFieldStatus formControlName="email" type="text">
   * <!-- Shows green border and styling when valid -->
   */
  public get showValid(): boolean {
    if (!this.touched) return false
    return !!this.control?.enabled && this.valid && this.ignoreStatus !== FormFieldStatus.Valid;
  }

  /**
   * @property inputElement
   * @description
   * **Gets the native HTML input element** for direct manipulation.
   *
   * This getter provides access to the underlying HTML input element, allowing
   * for direct DOM manipulation, styling, or event handling when needed.
   *
   * @type {HTMLInputElement}
   * @example
   * // Access the native element programmatically
   * const inputElement = this.formFieldStatus.inputElement;
   * inputElement.focus();
   * inputElement.select();
   */
  public get inputElement(): HTMLInputElement {
    return this.elementRef.nativeElement
  }

  /**
   * @constructor
   * @description
   * **Initializes the form field status directive** with optional form control and element reference.
   *
   * The constructor injects an optional `NgControl` for automatic form control detection
   * and an `ElementRef` for direct element access. The `NgControl` is optional to support
   * scenarios where the directive is used without a form control.
   *
   * @param {NgControl} _ngControl - Optional injected form control (optional)
   * @param {ElementRef<HTMLInputElement>} elementRef - Reference to the HTML input element
   * @example
   * // Automatically injected by Angular's dependency injection
   * constructor(
   *   @Optional() private _ngControl: NgControl,
   *   public elementRef: ElementRef<HTMLInputElement>
   * ) {}
   */
  constructor(
    @Optional()
    private readonly _ngControl: NgControl,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _destroyRef: DestroyRef,
    public readonly elementRef: ElementRef<HTMLInputElement>
  ) {}

  /**
   * @method ngOnInit
   * @description
   * **Initializes the form field status directive** with optional form control and element reference.
   *
   * The constructor injects an optional `NgControl` for automatic form control detection
   * and an `ElementRef` for direct element access. The `NgControl` is optional to support
   * scenarios where the directive is used without a form control.
   */
  ngOnInit(): void {
    
    // void
    if (this.control == null) return;

    this.control.statusChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(_ => this._cdr.markForCheck());

  }

}
